<script setup>
import { ref, useId, nextTick, onBeforeUnmount } from 'vue'
import { onClickOutside } from '@vueuse/core'
import Rating from '../Rating.vue'
import { isCurrent } from './navigation.js'

const props = defineProps({
    // [{ label, url, children?: [{ label, url, icon, description }], button?: { label, url } }]
    items: {
        type: Array,
        required: true,
    },
    contact: {
        type: Object,
        required: true,
    },
})

const rating = window.CursusGeregeld.rating
const baseId = useId()

const root = ref(null)
const triggers = ref([])

const openIndex = ref(null)
let openedAt = 0

function hasPanel (item) {
    return item.children?.length > 0
}

function open (index) {
    if (openIndex.value !== index) {
        openedAt = Date.now()
    }

    openIndex.value = hasPanel(props.items[index]) ? index : null
}

function close () {
    openIndex.value = null
}

function toggle (index) {
    // A click right after hover opened the panel means "open", not "close again"
    if (openIndex.value === index && Date.now() - openedAt > 400) {
        close()
    } else {
        open(index)
    }
}

// Hover intent: a short delay stops the panel from flashing open when the mouse only passes by,
// and from closing when it briefly leaves the menu on its way to the panel.
let timer = null

function clearTimer () {
    clearTimeout(timer)
}

function onPointerEnterItem (index, event) {
    // Touch and pen open the panel with a tap (click), otherwise a single tap would open and close it again
    if (event.pointerType !== 'mouse') return

    clearTimer()

    if (openIndex.value !== null) {
        open(index)
    } else {
        timer = setTimeout(() => open(index), 120)
    }
}

function onPointerLeaveMenu (event) {
    if (event.pointerType !== 'mouse') return

    clearTimer()
    timer = setTimeout(close, 250)
}

function closeAndFocusTrigger () {
    const index = openIndex.value
    if (index === null) return

    close()
    nextTick(() => triggers.value[index]?.focus())
}

// Keyboard users tabbing out of the menu close it as well
function onFocusOut (event) {
    if (!root.value?.contains(event.relatedTarget)) {
        close()
    }
}

onClickOutside(root, close)

onBeforeUnmount(clearTimer)

</script>

<template>
    <nav
        ref="root"
        aria-label="Hoofdnavigatie"
        class="relative hidden lg:block bg-white"
        @pointerenter="clearTimer"
        @pointerleave="onPointerLeaveMenu"
        @focusout="onFocusOut"
        @keydown.esc="closeAndFocusTrigger"
    >
        <div class="container max-w-7xl grid grid-cols-[1fr_auto_1fr] items-center">
            <div class="flex items-center">
                <Rating :rating="rating" />
            </div>

            <ul role="list" class="flex items-center gap-1">
                <!-- Not `relative`: the panel inside is positioned against the whole <nav>, so it spans the page width -->
                <li v-for="(item, index) in items" :key="item.url ?? item.label" @pointerenter="onPointerEnterItem(index, $event)">
                    <button
                        v-if="hasPanel(item)"
                        :ref="el => triggers[index] = el"
                        type="button"
                        class="relative flex items-center gap-1.5 px-4 py-4 text-sm font-medium transition-colors cursor-pointer focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-sky-700"
                        :class="openIndex === index || isCurrent(item) ? 'text-red-500' : 'text-gray-700 hover:text-red-500'"
                        :aria-expanded="openIndex === index"
                        :aria-controls="`${baseId}-panel-${index}`"
                        @click="toggle(index)"
                    >
                        {{ item.label }}
                        <icon name="chevronDown" class="size-4 transition-transform duration-200" :class="{ 'rotate-180': openIndex === index }"></icon>
                        <span aria-hidden="true" class="absolute inset-x-3 bottom-0 h-0.5 origin-center rounded-full bg-red-500 transition-transform duration-200" :class="openIndex === index || isCurrent(item) ? 'scale-x-100' : 'scale-x-0'"></span>
                    </button>
                    <a
                        v-else
                        :href="item.url"
                        class="relative flex items-center px-4 py-4 text-sm font-medium transition-colors focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-sky-700"
                        :class="isCurrent(item) ? 'text-red-500' : 'text-gray-700 hover:text-red-500'"
                        :aria-current="isCurrent(item, true) ? 'page' : undefined"
                    >
                        {{ item.label }}
                        <span aria-hidden="true" class="absolute inset-x-3 bottom-0 h-0.5 origin-center rounded-full bg-red-500 transition-transform duration-200" :class="isCurrent(item) ? 'scale-x-100' : 'scale-x-0'"></span>
                    </a>

                    <!-- Right after its button in the DOM, so Tab moves from the button straight into the panel -->
                    <Transition
                        v-if="hasPanel(item)"
                        enter-active-class="transition duration-200 ease-out motion-reduce:transition-none"
                        enter-from-class="opacity-0 -translate-y-1"
                        leave-active-class="transition duration-150 ease-in motion-reduce:transition-none"
                        leave-to-class="opacity-0 -translate-y-1"
                    >
                        <div v-if="openIndex === index" :id="`${baseId}-panel-${index}`" class="absolute inset-x-0 top-full z-25 border-t border-gray-100 bg-white shadow-xl">
                            <div class="container max-w-7xl grid grid-cols-[15rem_minmax(0,1fr)] gap-10 py-8">

                                <div class="border-r border-gray-100 pr-8">
                                    <p class="text-lg font-semibold text-gray-900">{{ item.label }}</p>
                                    <a
                                        v-if="item.button"
                                        :href="item.button.url"
                                        class="group mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-red-600 hover:text-red-700"
                                    >
                                        {{ item.button.label }}
                                        <icon name="ArrowRight" class="size-3.5 transition-transform group-hover:translate-x-0.5"></icon>
                                    </a>

                                    <div class="mt-8 rounded-lg bg-gray-50 p-4 text-sm">
                                        <p class="font-medium text-gray-900">Hulp nodig bij het kiezen van de juiste cursus?</p>
                                        <a :href="`tel:${contact.phoneNumber}`" class="mt-2 inline-flex items-center gap-2 font-semibold text-sky-700 hover:underline">
                                            <icon name="phone" class="size-4"></icon>{{ contact.phoneNumber }}
                                        </a>
                                    </div>
                                </div>

                                <ul role="list" class="grid grid-cols-2 xl:grid-cols-3 gap-1 content-start">
                                    <li v-for="child in item.children" :key="child.url">
                                        <a
                                            :href="child.url"
                                            class="group flex items-start gap-3 rounded-lg p-3 transition-colors hover:bg-gray-50 focus-visible:outline-2 focus-visible:outline-sky-700"
                                            :aria-current="isCurrent(child, true) ? 'page' : undefined"
                                        >
                                            <span class="grid size-10 flex-none place-items-center rounded-lg bg-red-50 text-red-500 transition-colors group-hover:bg-red-500 group-hover:text-white">
                                                <icon :name="child.icon" class="size-6"></icon>
                                            </span>
                                            <span class="min-w-0">
                                                <span class="block text-sm font-semibold text-gray-900">{{ child.label }}</span>
                                                <span v-if="child.description" class="mt-0.5 block text-xs leading-5 text-gray-500 line-clamp-2">{{ child.description }}</span>
                                            </span>
                                        </a>
                                    </li>
                                </ul>

                            </div>
                        </div>
                    </Transition>
                </li>
            </ul>

            <div></div>
        </div>
    </nav>
</template>
